import { Controller } from "@hotwired/stimulus"

// Web-layer mitigation for the mobile bottom-nav tab-switch flicker
// (CONEK-4562). The "real" fix — one persistent WebView per tab (Hotwire
// Native tabbed navigation) — is deferred to its own epic. Until then every
// tab tap does a full `Turbo.visit`, and the FIRST visit to a not-yet-seen tab
// pays a cold server round-trip that reads as a blank flash.
//
// This controller warms the browser's HTTP cache for the OTHER tab roots while
// the user is idle on the current tab, so that first `Turbo.visit` is served
// from cache instead of the network. We use `<link rel="prefetch">` — the same
// primitive Turbo Drive itself uses for hover-prefetch — rather than a hidden
// `<turbo-frame>` (which fetches a fragment, not a full-page document) or the
// `Turbo.cache` API (which has no public "insert snapshot for URL X" method in
// Turbo 8). Prefetch degrades gracefully: a WebView that ignores the hint just
// falls back to today's behaviour, and it never blocks the main thread.
//
// Fed by the mobile layout with the resolved tab-root items (same
// MobileNavRegistry payload the native bottom-nav bridge consumes) plus the
// current request path, so the active tab is never prefetched and the set can
// never drift from the real nav.
export default class extends Controller {
  static values = {
    // [{ key, label, icon, path, active }] — the resolved tab-root nav items.
    items: Array,
    // The current request path; the item whose `path` matches is skipped.
    currentPath: String,
    // Server-decided (MobileNavigationHelper#mobile_tab_prefetch_enabled?).
    // False in the Android app, which keeps one WebView per tab, so each tab
    // loads its own root and a prefetch from here is a duplicate (CONEK-9487).
    enabled: { type: Boolean, default: true },
  }

  connect() {
    if (!this.enabledValue) return

    this.#scheduleIdle(() => this.#prefetchInactiveTabs())
  }

  disconnect() {
    if (this.idleHandle != null) {
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(this.idleHandle)
      } else {
        clearTimeout(this.idleHandle)
      }
      this.idleHandle = null
    }
  }

  // Insert a `<link rel="prefetch">` into <head> for every tab root that is not
  // the current one. Skips duplicates so a re-connect (Turbo Drive restore)
  // never stacks identical hints.
  #prefetchInactiveTabs() {
    const current = this.#normalizePath(this.currentPathValue)

    this.#tabPaths()
      .filter((path) => this.#normalizePath(path) !== current)
      .forEach((path) => this.#prefetch(path))
  }

  // Extracts the destination paths from the item payload. Tolerates items that
  // are missing a `path` (e.g. a malformed entry) rather than throwing.
  #tabPaths() {
    return this.itemsValue
      .map((item) => item && item.path)
      .filter((path) => typeof path === "string" && path.length > 0)
  }

  #prefetch(path) {
    if (this.#alreadyPrefetched(path)) return

    const link = document.createElement("link")
    link.rel = "prefetch"
    link.href = path
    link.dataset.tabPrefetch = "true"
    document.head.appendChild(link)
  }

  #alreadyPrefetched(path) {
    return Boolean(
      document.head.querySelector(
        `link[rel="prefetch"][data-tab-prefetch="true"][href="${CSS.escape(path)}"]`,
      ),
    )
  }

  // Runs the work when the browser is idle. `requestIdleCallback` is missing in
  // Safari and older Android WebViews, so we fall back to a short `setTimeout`
  // — the point is only to stay off the critical path of the initial paint.
  #scheduleIdle(callback) {
    if (typeof window.requestIdleCallback === "function") {
      this.idleHandle = window.requestIdleCallback(callback, { timeout: 2000 })
    } else {
      this.idleHandle = setTimeout(callback, 500)
    }
  }

  // Drops a single trailing slash so `/mobile/companies` and
  // `/mobile/companies/` compare equal (mirrors the registry's tab-root match).
  #normalizePath(path) {
    if (typeof path !== "string") return ""
    return path.length > 1 ? path.replace(/\/$/, "") : path
  }
}
